import React, { FC, ReactElement, RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ButtonBase, Grow } from '@material-ui/core'
import IconButton, { IconButtonType } from '../../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../../React/Common/Components/Icon'
import {
    IsStringEmpty,
    isStringNotEmpty,
    IsVarEmpty,
    isVarNotEmpty,
    Or
} from '../../../../../NonReact/utils/GlobalFunctions'
import { UserBadge } from '../../../../../React/Common/Components/UserBadge/UserBadge'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { NotificationIcon } from '../../../../../React/MainMenu/NotificationIcon'
import { AppMenu } from './AppMenu'
import { useDarkMode } from '../../../../../React/Common/Hooks/useDarkMode'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import { useIsInsightsAvailable } from '../../../../Hooks/useIsInsightsAvailable'
import { useIsPresenceAvailable } from '../../../../Hooks/useIsPresenceAvailable'
import { useCheckUserHasPermissions } from '../../../../Hooks/useCheckPermissions'
import { ADMIN_MENU_PERMISSIONS, STATUS_AND_TOOLS_PERMISSIONS } from '../../../../../React/Common/Utils/PermissionsUtil'
import { AppMenu as AppMenuLanguage } from '../../../../../../i18n'
import { MainMenuApp, MainMenuAppTransfer } from '../../../../../NonReact/constants/AppMenuConstants'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { appMenuUpdate$ } from '../../../../../NonReact/common/RxJS/AppMenuState'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { useCurrentPage } from '../../../../Hooks/useCurrentPage'
import { configureFullURLForLocalDevelopment } from '../../../../../React/Common/Utils/LocalUtils'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { userPagePermissions$ } from '../../../../../NonReact/common/RxJS/UserPagePermissionsState'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { UserMenu } from './UserMenu'
import { TransitionPopper } from '../../TransitionPopper/TransitionPopper'
import { AppMenuEntry, RouteID } from '../Types/Types'
import { useUserHasSelfProvisioningPermission } from '../../../../../React/Common/Hooks/permissions/useUserHasSelfProvisioningPermission'
import { useUserHasManageQueuePermission } from '../../../../../React/Common/Hooks/permissions/useUserHasManageQueuesPermission'
import { useUserHasUserManagementPermission } from '../../../../../React/Common/Hooks/permissions/useUserHasUserManagementPermission'
import { Page } from '../../../../../NonReact/constants/Components/Page'

type Props = {
    routeID?: RouteID
}

export const HeaderTop: FC<Props> = ({ routeID }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AppMenuLanguage.NAME)

    useDarkMode()

    const [userInfo]: UserInfo = useUserInfo()

    const currentJSPPage: Nullable<Page> = useCurrentPage()

    const branding: Nullable<Branding> = useBranding()

    const anchorElAppLauncher: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)
    const anchorElUserMenu: RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

    const [isUserMenuOpen, setIsUserMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const [isAppMenuOpen, setIsAppMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const [notificationsOpen, setNotificationsOpen]: ReactState<boolean> = useState<boolean>(false)

    // Should we show the Reporting entry?
    // ---
    // User has reporting access?
    const hasReportingAccess: Definable<boolean> = useMemo((): Definable<boolean> => {
        if (IsVarEmpty(userInfo)) {
            return undefined
        }

        return userInfo?.Scope?.ReportingAccess !== ReportingAccessLevels.NONE
    }, [userInfo])

    // Is Insights available for the user?
    const isInsightsAvailable: Definable<boolean> = useIsInsightsAvailable()

    // Is Presence available for the user?
    const isPresenceAvailable: Definable<boolean> = useIsPresenceAvailable()

    // Reporting Available?
    const isReportingAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> => Or(hasReportingAccess, isInsightsAvailable, isPresenceAvailable),
        [hasReportingAccess, isInsightsAvailable, isPresenceAvailable]
    )

    const isBroadworksProvisioningWizardAvailable: Definable<boolean> = useUserHasSelfProvisioningPermission()
    const isQueueManagementAvailable: Definable<boolean> = useUserHasManageQueuePermission()
    const isUserManagementAvailable: Definable<boolean> = useUserHasUserManagementPermission()

    // Fast Provisioning Available?
    const isFastProvisioningAvailable: Definable<boolean> = useMemo(
        (): Definable<boolean> =>
            Or(isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable),
        [isBroadworksProvisioningWizardAvailable, isQueueManagementAvailable, isUserManagementAvailable]
    )

    // Should we show Admin entry?
    // ---
    // User has access to Admin?
    const isAdminAvailable: Definable<boolean> = useCheckUserHasPermissions(ADMIN_MENU_PERMISSIONS, true)

    // Should we show Status and Tools entry?
    // ---
    // User has access to Status and Tools?
    const isStatusAndToolsAvailable: Definable<boolean> = useCheckUserHasPermissions(STATUS_AND_TOOLS_PERMISSIONS, true)

    // What options are shown in the menu?
    const appMenuEntries: Definable<AppMenuEntry[]> = useMemo((): Definable<AppMenuEntry[]> => {
        if (
            isVarNotEmpty(isReportingAvailable) &&
            isVarNotEmpty(isFastProvisioningAvailable) &&
            isVarNotEmpty(isAdminAvailable) &&
            isVarNotEmpty(isStatusAndToolsAvailable)
        ) {
            return [
                {
                    enabled: isReportingAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Reporting),
                    icon: IconName.REPORTING,
                    menu: MainMenuApp.REPORTING,
                    pages: [RouteID.REPORTING_PORTAL, RouteID.INSIGHTS, RouteID.PRESENCE],
                    oldPages: [Page.REPORTING, Page.PRESENCE, Page.INSIGHTS_HUB]
                },
                {
                    enabled: isFastProvisioningAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_FastProvisioning),
                    icon: IconName.FAST_PROVISIONING,
                    menu: MainMenuApp.FAST_PROVISIONING
                },
                {
                    enabled: isAdminAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_Administration),
                    icon: IconName.ADMINISTRATION,
                    menu: MainMenuApp.ADMIN,
                    pages: [RouteID.CLIENT_CONFIGURATION, RouteID.CRM_INTEGRATION],
                    oldPages: [
                        Page.ADMIN_DEVICES,
                        Page.ADMIN_AGENTS,
                        Page.ADMIN_PARTITIONS,
                        Page.ADMIN_TELSYSS,
                        Page.ADMIN_CMENVIRONMENTS,
                        Page.ADMIN_USERS,
                        Page.ADMIN_CODES,
                        Page.ADMIN_CALLRECINTS,
                        Page.ADMIN_MESSAGES,
                        Page.ADMIN_DATABASE,
                        Page.ADMIN_DIRECTORY
                    ]
                },
                {
                    enabled: isStatusAndToolsAvailable,
                    title: language.get(AppMenuLanguage.AppMenu_Apps_StatusAndTools),
                    icon: IconName.STATUS_TOOLS,
                    menu: MainMenuApp.STATUS,
                    oldPages: [
                        Page.LICENSEMGR,
                        Page.LOGFILEMGR,
                        Page.ADMIN_LOGGING,
                        Page.TELMODULE_CALLCTRL,
                        Page.TELMODULE_STATUS,
                        Page.APP_STATUS,
                        Page.AUDITTRAIL
                    ]
                }
            ]
        }

        return []
    }, [
        isReportingAvailable,
        isFastProvisioningAvailable,
        isAdminAvailable,
        isStatusAndToolsAvailable,
        routeID,
        language.getLanguageID()
    ])

    const currentPage: Definable<MainMenuApp> = useMemo(
        (): Definable<MainMenuApp> =>
            (
                appMenuEntries?.find(
                    (entry: AppMenuEntry): boolean =>
                        !!(
                            (isStringNotEmpty(routeID) && entry.pages?.includes(routeID)) ||
                            (isStringNotEmpty(currentJSPPage) && entry.oldPages?.includes(currentJSPPage))
                        )
                ) ?? appMenuEntries?.[0]
            )?.menu,
        [JSON.stringify(appMenuEntries), routeID, currentJSPPage]
    )

    const appCount: number = useMemo(
            (): number => appMenuEntries?.filter(({ enabled }: AppMenuEntry): boolean => enabled)?.length ?? 0,
            [JSON.stringify(appMenuEntries)]
        )

        const showAppMenu: boolean = useMemo((): boolean => appCount > 1, [appCount])

        const appMenuState: RxJSCurrentState<MainMenuAppTransfer> = useRxJSState(appMenuUpdate$, {
            app: currentPage ?? MainMenuApp.REPORTING,
            showAppMenu
        })

        useEffect((): void => {
            appMenuUpdate$.announce({
                app: currentPage ?? MainMenuApp.REPORTING,
                showAppMenu
            })
        }, [appCount])

    useEffect((): void => {
        if (
            isVarNotEmpty(hasReportingAccess) &&
            isVarNotEmpty(isInsightsAvailable) &&
            isVarNotEmpty(isPresenceAvailable) &&
            isVarNotEmpty(isUserManagementAvailable) &&
            isVarNotEmpty(isQueueManagementAvailable) &&
            isVarNotEmpty(isBroadworksProvisioningWizardAvailable)
        ) {
            userPagePermissions$.announce({
                reporting: {
                    reports: hasReportingAccess ?? false,
                    insights: isInsightsAvailable ?? false,
                    presence: isPresenceAvailable ?? false
                },
                fastProvisioning: {
                    userManagement: isUserManagementAvailable ?? false,
                    queueManagement: isQueueManagementAvailable ?? false,
                    selfProvisioning: isBroadworksProvisioningWizardAvailable ?? false
                }
            })
        }
    }, [
        hasReportingAccess,
        isInsightsAvailable,
        isPresenceAvailable,
        isUserManagementAvailable,
        isQueueManagementAvailable,
        isBroadworksProvisioningWizardAvailable
    ])

    return (
        <>
            <div className='rh-left'>
                {showAppMenu ? (
                    <div className='rh-menu-container'>
                        <IconButton
                            ref={anchorElAppLauncher}
                            iconName={IconName.MENU_LAUNCHER}
                            type={IconButtonType.BUTTON}
                            onClick={(): void => setIsAppMenuOpen((prev: boolean): boolean => !prev)}
                        />

                    <TransitionPopper
                        className='app-menu-popper-container'
                        isOpen={isAppMenuOpen}
                        setIsOpen={setIsAppMenuOpen}
                        anchorEl={anchorElAppLauncher}
                        Transition={Grow}
                        style={{ width: 'clamp(300px, 90%, 450px)' }}
                    >
                        <div className='app-menu-popper'>
                            <AppMenu
                                appMenuState={appMenuState}
                                appMenuEntries={appMenuEntries}
                                closeMenu={(): void => setIsAppMenuOpen(false)}
                            />
                        </div>
                    </TransitionPopper>
                </div>
            ) : (
                <></>
            )}
                {IsStringEmpty(branding?.BrandingConfig?.imagery?.squareLogo) &&
                isStringNotEmpty(branding?.BrandingConfig?.imagery?.mainLogo) ? (
                    <a
                        href={branding?.BrandingConfig?.links?.mainSiteLink?.url ?? EMPTY_STRING}
                        target={branding?.BrandingConfig?.links?.mainSiteLink?.target}
                        title={branding?.BrandingConfig?.links?.mainSiteLink?.title}
                        className='rh-logo-container'
                    >
                        <img
                            className='rh-logo'
                            src={configureFullURLForLocalDevelopment(branding.BrandingConfig.imagery.mainLogo)}
                            alt='Main Logo'
                        />
                    </a>
                ) : (
                    <></>
                )}
            </div>
            <div className='rh-right'>
                {isStringNotEmpty(userInfo?.FullName) ? (
                    <>
                        <ButtonBase
                            id='user-menu-button'
                            ref={anchorElUserMenu}
                            onClick={(): void => setIsUserMenuOpen((prev: boolean): boolean => !prev)}
                        >
                            <div className='rh-settings-container'>
                                <UserBadge name={userInfo?.FullName} />
                            </div>
                        </ButtonBase>

                        <TransitionPopper
                            className='user-menu-popper-container'
                            isOpen={isUserMenuOpen}
                            setIsOpen={setIsUserMenuOpen}
                            anchorEl={anchorElUserMenu}
                            Transition={Grow}
                            style={{ width: 'clamp(200px, 100%, 300px)' }}
                            clickAwayGuard={(event: MouseEvent): boolean =>
                                !(event.target as HTMLElement).closest('#user-menu-button')
                            }
                        >
                            <div className='user-menu-popper' style={{ transformOrigin: 'top' }}>
                                <UserMenu
                                    closeMenu={(): void => {
                                        setNotificationsOpen(true)
                                        setIsUserMenuOpen(false)
                                    }}
                                />
                            </div>
                        </TransitionPopper>
                        <NotificationIcon isOpenState={[notificationsOpen, setNotificationsOpen]} disableIcon={true} />
                    </>
                ) : (
                    <></>
                )}
            </div>
        </>
    )
}
